import { SearchTaskFilter, SearchTaskResultExternal } from '@askporter/client-grieg-lyric';
import { API } from '../api';

export interface WaitForLinkedStateOptions {
  /** The page's own scope, e.g. `{ linkedAsset: [assetUid] }`. Must not include user-selected filters. */
  scopeFilter: SearchTaskFilter;
  taskUids: Array<string>;
  expect: 'present' | 'absent';
  /** Stops polling early, e.g. when the page unmounts */
  signal?: AbortSignal;
}

// task search is updated asynchronously, roughly 2.8-4s after a relationship write, and large batches arrive
// in bursts over several more seconds
const INITIAL_DELAY_MS = 2500;
const INTERVAL_MS = 750;
const STALL_MS = 8000;
const MAX_MS = 30000;
// each copy of the search index refreshes on its own ~1s cycle, so one read can see the change before the
// copy that serves the next request does
const SETTLE_MS = 1000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Polls task search, narrowed to `taskUids` within `scopeFilter`, until every task is linked (`present`) or none
 * are (`absent`). Keeps going while more tasks are catching up, and gives up once nothing has changed for a while.
 * Failed searches count as no progress.
 *
 * @returns whether search reflected the whole change before giving up or being aborted. Waits a further second
 * after confirming so every copy of the index has caught up before the caller refetches.
 */
export const waitForLinkedState = async ({
  scopeFilter,
  taskUids,
  expect,
  signal,
}: WaitForLinkedStateOptions): Promise<boolean> => {
  const countDone = async (): Promise<number> => {
    try {
      const { data } = await API().post({
        path: 'tasks/search',
        payload: { page: 1, pageSize: 1, filter: { ...scopeFilter, uid: taskUids } },
        additionalHeaders: { 'x-usecase': 'filtered_search' },
      });
      const totalResults = (data as SearchTaskResultExternal)?.summary?.totalResults ?? 0;
      return expect === 'present' ? totalResults : taskUids.length - totalResults;
    } catch {
      return -1;
    }
  };

  await sleep(INITIAL_DELAY_MS);
  if (signal?.aborted) return false;
  const startedAt = Date.now();
  let lastProgressAt = startedAt;
  let best = -1;
  for (;;) {
    const done = await countDone();
    if (signal?.aborted) return false;
    if (done === taskUids.length) {
      await sleep(SETTLE_MS);
      return true;
    }
    if (done > best) {
      best = done;
      lastProgressAt = Date.now();
    }
    if (Date.now() - lastProgressAt >= STALL_MS || Date.now() - startedAt >= MAX_MS) return false;
    await sleep(INTERVAL_MS);
    if (signal?.aborted) return false;
  }
};

export const taskUidFromURN = (urn: string): string => urn.split(':').pop() ?? urn;
